/**
 * User Profile Hook
 *
 * Custom hook encapsulating profile state, polling, optimistic credit
 * deduction, and activity-data aggregation from the Jobs API.
 *
 * Uses React Query for caching so that profile data persists across
 * component mounts (e.g., navigating between DualLayoutRoute and
 * AuthenticatedLayoutRoute does not trigger a refetch if data is fresh).
 *
 * - Fetches profile when authenticated (cached via React Query)
 * - Polls profile every 60 seconds via refetchInterval
 * - deductCredits() applies optimistic update, reverts on failure
 * - activityData derived from completed jobs (last 7 calendar days)
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 9.1, 9.2, 9.3
 */

import { useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { UserProfileService } from '../services/user-profile-service';
import { JobService } from '../services/job-service';
import { isLocalDev } from '../utils/is-local-dev';
import { MOCK_USER_PROFILE, MOCK_ACTIVITY_DATA } from '../services/mock-data';
import type { UserProfile, ActivityChartDataPoint } from '../types';
import { PROFILE_POLL_INTERVAL_MS, ACTIVITY_CHART_DAYS } from '../config/constants';

const POLL_INTERVAL = PROFILE_POLL_INTERVAL_MS;
const ACTIVITY_DAYS = ACTIVITY_CHART_DAYS;

/** Query keys for user profile cache */
const PROFILE_QUERY_KEY = ['userProfile'] as const;
const ACTIVITY_QUERY_KEY = ['userActivity'] as const;

/**
 * Build an array of 7 ActivityChartDataPoints (one per day, last 7 days)
 * from a list of completed jobs. Uses the user's local timezone.
 */
export function aggregateActivityData(
    jobs: Array<{ created_timestamp: number; credits_consumed?: number }>,
): ActivityChartDataPoint[] {
    const now = new Date();
    const days: ActivityChartDataPoint[] = [];

    for (let i = ACTIVITY_DAYS - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        days.push({ date: label, count: 0, creditsUsed: 0 });
    }

    // Local timezone day boundary
    const dayStart = (date: Date): number => {
        const copy = new Date(date);
        copy.setHours(0, 0, 0, 0);
        return copy.getTime();
    };

    const todayStart = dayStart(now);
    const windowStart = todayStart - (ACTIVITY_DAYS - 1) * 86400000;

    for (const job of jobs) {
        const ts = job.created_timestamp;
        if (ts < windowStart || ts >= todayStart + 86400000) continue;

        const jobDayStart = dayStart(new Date(ts));
        const idx = Math.floor((jobDayStart - windowStart) / 86400000);
        if (idx >= 0 && idx < ACTIVITY_DAYS) {
            days[idx].count += 1;
            days[idx].creditsUsed += job.credits_consumed ?? 0;
        }
    }

    return days;
}

// Singleton service instances (shared across hook instances)
let profileService: UserProfileService | null = null;
let jobService: JobService | null = null;

function getProfileService(): UserProfileService {
    if (!profileService) {
        profileService = new UserProfileService();
    }
    return profileService;
}

function getJobService(): JobService {
    if (!jobService) {
        jobService = new JobService();
    }
    return jobService;
}

export function useUserProfile() {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const selfDispatchRef = useRef(false);

    // Profile query with polling
    const {
        data: profile = null,
        isLoading: profileLoading,
        error: profileError,
    } = useQuery<UserProfile | null, Error>({
        queryKey: PROFILE_QUERY_KEY,
        queryFn: async () => {
            if (isLocalDev() && !isAuthenticated) {
                return { ...MOCK_USER_PROFILE } as UserProfile;
            }
            const service = getProfileService();
            return await service.getProfile();
        },
        enabled: isAuthenticated || isLocalDev(),
        staleTime: POLL_INTERVAL, // Data is fresh for the poll interval
        refetchInterval: isAuthenticated ? POLL_INTERVAL : false,
        refetchIntervalInBackground: false,
    });

    // Activity data query
    const {
        data: activityData = [],
        isLoading: activityLoading,
    } = useQuery<ActivityChartDataPoint[], Error>({
        queryKey: ACTIVITY_QUERY_KEY,
        queryFn: async () => {
            if (isLocalDev() && !isAuthenticated) {
                return [...MOCK_ACTIVITY_DATA];
            }
            const service = getJobService();
            const result = await service.listJobs({ includeDeleted: true });
            return aggregateActivityData(result.items);
        },
        enabled: isAuthenticated || isLocalDev(),
        staleTime: POLL_INTERVAL,
    });

    // Listen for profile-refresh events from other components
    useEffect(() => {
        const handler = () => {
            if (!selfDispatchRef.current) {
                queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
            }
        };
        window.addEventListener('profile-refresh', handler);
        return () => window.removeEventListener('profile-refresh', handler);
    }, [queryClient]);

    // Dispatch profile-refresh event when profile data loads successfully
    useEffect(() => {
        if (profile && !profileLoading) {
            selfDispatchRef.current = true;
            window.dispatchEvent(new Event('profile-refresh'));
            selfDispatchRef.current = false;
        }
    }, [profile, profileLoading]);

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: ACTIVITY_QUERY_KEY });
        selfDispatchRef.current = true;
        window.dispatchEvent(new Event('profile-refresh'));
        selfDispatchRef.current = false;
    }, [queryClient]);

    const deductCredits = useCallback(async (amount: number) => {
        const currentProfile = queryClient.getQueryData<UserProfile | null>(PROFILE_QUERY_KEY);
        if (!currentProfile) return;

        // Optimistic update
        queryClient.setQueryData<UserProfile | null>(PROFILE_QUERY_KEY, (curr) =>
            curr ? { ...curr, credits_remaining: curr.credits_remaining - amount } : curr,
        );

        try {
            const service = getProfileService();
            const updated = await service.deductCredits(amount);
            queryClient.setQueryData<UserProfile | null>(PROFILE_QUERY_KEY, updated);
        } catch {
            // Revert on failure
            queryClient.setQueryData<UserProfile | null>(PROFILE_QUERY_KEY, currentProfile);
        }
    }, [queryClient]);

    const isLoading = profileLoading || activityLoading;
    const error = profileError ? profileError.message : null;

    return {
        profile,
        isLoading,
        error,
        activityData,
        refresh,
        deductCredits,
    };
}
